import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ApiService } from '@domgen/data-access-claims';
import { AppConfig, Claim } from '@domgen/dgx-components';
import { mergeMap, Subject, takeUntil } from 'rxjs';
import { SmartFixAnalyticsService } from '../../../services/smart-fix-analytics/smart-fix-analytics.service';
import { CONFIG } from '@domgen/dgx-fe-config';
import {
  chatBotFadeInAnimation,
  chatBotFadeInAnimationSequence,
} from '../../../animations/chatAnimations';

export interface FeedbackOption {
  label: string;
  icon?: any;
  value: boolean | null;
  children?: FeedbackOption[];
  message?: string;
  callBack?: any;
  skipTracking?: boolean;
}

@Component({
  selector: 'smart-fix-bedrock-feedback',
  templateUrl: './bedrock-feedback.component.html',
  styleUrls: ['./bedrock-feedback.component.scss'],
  animations: [chatBotFadeInAnimationSequence],
})
export class BedrockFeedbackComponent implements OnInit, OnChanges, OnDestroy {
  @Input() feedback: string = '';
  @Input() sessionId!: string;
  @Output() requestFeedback = new EventEmitter<void>();
  @Output() closeChat = new EventEmitter<void>();
  private destroy$ = new Subject<void>();
  productType: string | undefined;

  onRequestFeedback() {
    this.requestFeedback.emit();
  }
  @Input() claim!: Claim;
  constructor(
    private smartFixAnalyticsService: SmartFixAnalyticsService,
    private apiService: ApiService,
    @Inject(CONFIG) protected readonly config: AppConfig
  ) {}

  ngOnInit() {
    this.productType =
      this.claim?.reflect?.productType?.toLowerCase() || 'appliance';
  }

  ngOnChanges() {
    if (this.feedback) {
      const feedbackOption = this.selectedOption?.children?.[0];
      if (feedbackOption) {
        feedbackOption.label = this.feedback;
        this.processOption(feedbackOption);
      }
      this.saveChatStatus();
    }
  }

  markResolved() {
    this.isResolved = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  readonly myAccountCTALabel = 'End, return to My Account';
  readonly continueRepairCTALabel = 'Continue to engineer booking';

  feedbackOptions: FeedbackOption[] = [
    {
      label: `Yes, it was useful`,
      value: null,
      callBack: this.markResolved.bind(this),
      message:
        'We’re happy we could help. So we can improve our service in future, we’d love to hear more about your experience. Would you like to provide further feedback?',
      children: [
        {
          label: `Yes, I want to give feedback`,
          value: null,
          message: `Great, let us know how your experience was.`,
          children: [
            {
              label: '',
              value: null,
              message: `Thanks for your feedback. You can now return to My Account or continue engineer booking if your issue continues.`,
              skipTracking: true,
              children: [
                {
                  skipTracking: true,
                  label: this.myAccountCTALabel,
                  value: null,
                  callBack: this.returnMyAccount.bind(this),
                },
                {
                  skipTracking: true,
                  label: this.continueRepairCTALabel,
                  value: null,
                  callBack: this.continueRepairBooking.bind(this),
                },
              ],
            },
          ],
        },
        {
          label: `No, end the chat`,
          value: null,
          message: `No problem. Let us know if you want to return to My Account or continue engineer booking.`,
          callBack: this.saveChatStatus.bind(this),
          children: [
            {
              skipTracking: true,
              label: this.myAccountCTALabel,
              value: null,
              callBack: this.returnMyAccount.bind(this),
            },
            {
              skipTracking: true,
              label: this.continueRepairCTALabel,
              value: null,
              callBack: this.continueRepairBooking.bind(this),
            },
          ],
        },
      ],
    },
    {
      label: `No, it wasn't useful`,
      value: null,
      message: `We're sorry to hear that. We recommend getting an engineer out to look at your appliance. Once the chat has ended, we'll take you to our repair booking page. But first, how did we do today?`,
      children: [
        {
          label: ``,
          value: null,
          message: `Thanks for your feedback. Please close end this chat and we will take you to your repair booking.`,
          skipTracking: true,
          children: [
            {
              skipTracking: true,
              label: 'End, continue to repair booking',
              value: null,
              callBack: this.continueRepairBooking.bind(this),
            },
          ],
        },
      ],
    },
  ];

  window = window;
  isResolved = false;

  processOption(option: FeedbackOption) {
    option.value = true;
    if (option.children?.length === 1 && option.children[0].label === '') {
      this.onRequestFeedback();
    }

    const eventCategory = 'SF genAI';
    const label = option.label;
    const action = 'feedback';
    if (!option.skipTracking) {
      this.trackFeedbackChoice(label, action, eventCategory);
    }

    if (option.callBack) {
      option.callBack(option);
    }
  }

  async returnMyAccount(option?: FeedbackOption) {
    const eventCategory = 'SF genAI';
    const label = option?.label || this.myAccountCTALabel;
    const action = 'chatbot-completed';
    this.trackFeedbackChoice(label, action, eventCategory);
    this.smartFixAnalyticsService.clearSmartFixRoutingCookie();
    this.window.location.href = this.config.myAccountUrl;
  }

  continueRepairBooking(option?: FeedbackOption) {
    const eventCategory = 'SF genAI';
    const label = option?.label || this.continueRepairCTALabel;
    const action = 'chatbot-completed';
    this.trackFeedbackChoice(label, action, eventCategory);
    this.closeChat.emit();
  }

  saveChatStatus() {
    this.sendFeedback()
      .pipe(
        mergeMap(() => this.markAsComplete()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  sendFeedback() {
    return this.apiService.addBedrockChatFeedback(
      this.isResolved,
      this.feedback,
      'not set',
      this.sessionId
    );
  }
  markAsComplete() {
    return this.apiService.markBedrockChatComplete(this.sessionId);
  }

  get selectedOption() {
    return this.selectedOptions[this.selectedOptions.length - 1];
  }

  getSelectedOptions(
    optionTree: FeedbackOption[],
    selectedOptions: FeedbackOption[] = []
  ) {
    const selected = optionTree.find((option) => option.value === true);
    if (selected) {
      selectedOptions.push(selected);
      if (selected.children) {
        this.getSelectedOptions(selected.children, selectedOptions);
      }
    }
    return selectedOptions;
  }

  get selectedOptions() {
    return this.getSelectedOptions(this.feedbackOptions);
  }

  get optionsToDisplay() {
    const selected = this.selectedOption;
    if (!selected) return this.feedbackOptions;
    return selected.children;
  }

  trackFeedbackChoice(
    eventLabel: string,
    eventAction: string,
    eventCategory?: string
  ) {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      eventLabel,
      'fault-details-completed',
      this.claim,
      eventAction,
      eventCategory,
      'claims-sf-genAi'
    );
  }
}
